import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { Navbar } from '../Landing/components/Navbar';
import { Footer } from '../Landing/components/Footer';

export interface TocSection {
  id: string;
  title: string;
}

interface LegalPageLayoutProps {
  title: string;
  subtitle: string;
  effectiveDate: string;
  lastUpdated: string;
  sections: TocSection[];
  children: React.ReactNode;
}

export default function LegalPageLayout({
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  sections,
  children,
}: LegalPageLayoutProps) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? '');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const location = useLocation();

  // Scroll to hash on navigation (e.g. /privacy#cookies from the cookie banner)
  useEffect(() => {
    const hash = location.hash;
    if (!hash) return;

    const scrollToHash = () => {
      const id = decodeURIComponent(hash.slice(1));
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return true;
      }
      return false;
    };

    // Try immediately, then retry a few times for sections that may not be mounted yet
    if (scrollToHash()) return;

    let attempts = 0;
    const maxAttempts = 10;
    const tryScroll = () => {
      attempts++;
      if (scrollToHash() || attempts >= maxAttempts) return;
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  }, [location.hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTocOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-600">
      <Navbar />

      <main className="container mx-auto px-4 py-12 relative">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Mobile TOC Accordion */}
          <div className="lg:hidden mb-8">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 text-gray-900 font-medium"
            >
              <span>{t('legal.toc')}</span>
              <ChevronDown
                className={cn('w-5 h-5 transition-transform', tocOpen && 'rotate-180')}
              />
            </button>
            {tocOpen && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg p-4 space-y-2 max-h-96 overflow-y-auto">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      'block w-full text-left text-sm py-2 px-2 rounded-md transition-colors',
                      activeSection === section.id
                        ? 'bg-teal-500/10 text-teal-400 font-medium'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
              <h2 className="text-gray-900 font-semibold mb-6 px-3">{t('legal.toc')}</h2>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      'block w-full text-left text-sm py-1.5 px-3 border-l-2 transition-colors',
                      activeSection === section.id
                        ? 'border-teal-400 text-teal-400 font-medium bg-teal-500/5'
                        : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-200'
                    )}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">
            <div className="mb-12">
              <h1 className="text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">{title}</h1>
              <p className="text-lg text-gray-500 mb-4">{subtitle}</p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20">
                  {t('legal.effective', { date: effectiveDate })}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                  {t('legal.lastUpdated', { date: lastUpdated })}
                </span>
              </div>
            </div>

            <div className="bg-white/50 border border-gray-200 rounded-xl p-8 lg:p-10 space-y-16 [&_[id]]:scroll-mt-28">
              {children}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-teal-500 hover:bg-teal-400 text-white rounded-full p-3 shadow-lg z-50 transition-all transform hover:scale-110"
          aria-label={t('legal.backToTop')}
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
